/*
Copyright (C) 2021  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { css } from "styled-components";

const exactWidth = (width: string) => css`
  min-width: ${width};
  max-width: ${width};
`;

const exactHeight = (height: string) => css`
  min-height: ${height};
  max-height: ${height};
`;

/*
 * SwiftIQ palette: the Tech9IQ Ops Console tokens (nutanix_portal/public/assets/tech9-ui.css),
 * so SwiftIQ reads as one product family with the rest of Tech9IQ. Each value is a CSS variable
 * defined in ./tokens.ts, light on :root and dark on :root[data-theme="dark"], so the same
 * styled-components rules follow the theme. Keys keep their original meaning for the components
 * that use them; the hex in each comment is the light value.
 * (Canvas code cannot read var(): it resolves them with cssColor() from utils/ThemeMode.)
 */
export const ThemePalette = {
  primary: "var(--t9-primary)", // #4C6575 brand-600: buttons, links, selection
  primaryLight: "var(--t9-primary-hover)", // #3D525F brand-700
  secondary: "var(--t9-border)", // #E2E8F0 slate-200
  secondaryLight: "var(--t9-text-secondary)", // #64748B slate-500
  black: "var(--t9-text)", // #0F172A slate-900: the text colour
  alert: "var(--t9-danger-700)", // #C0392B
  success: "var(--t9-success-500)", // #71C173 Brand Green
  warning: "var(--t9-warning-500)", // #F39C12
  accent: "var(--t9-accent-500)", // #46C4F2 Bright Cyan: focus rings, highlights, chart accent
  transfer: "var(--t9-info-700)", // #1A7AA8: the replica/transfer accent; migrations use primary
  brandSoft: "var(--t9-selected-bg)", // #EEF4F7 brand-50
  /** Cards, panels, menus, inputs: white in light, the dark pane in dark. */
  surface: "var(--t9-bg-surface)",
  /** Text and icons on a coloured fill (buttons, solid badges): white in both themes. */
  onFill: "var(--t9-text-on-fill)",
  grayscale: [
    "var(--t9-border)", // 0 borders (slate-200)
    "var(--t9-bg-sunken)", // 1 muted surfaces, hovers (slate-100)
    "var(--t9-border-control)", // 2 control borders (slate-300)
    "var(--t9-text-faint)", // 3 faint text, icons (slate-400)
    "var(--t9-text-muted)", // 4 body/secondary text (slate-600)
    "var(--t9-text-secondary)", // 5 secondary text (slate-500)
    "var(--t9-text-title2)", // 6 strong text (slate-800)
    "var(--t9-bg-page)", // 7 page background (slate-50)
    "var(--t9-text-secondary)", // 8 muted text (slate-500)
  ],
};

/*
 * The fixed light values ThemePalette had before it followed the theme, kept for reference.
 * export const ThemePaletteLight = { primary: "#4C6575", primaryLight: "#3D525F",
 *   secondary: "#E2E8F0", secondaryLight: "#64748B", black: "#0F172A", alert: "#C0392B",
 *   success: "#71C173", warning: "#F39C12", accent: "#46C4F2", transfer: "#1A7AA8",
 *   brandSoft: "#EEF4F7", grayscale: ["#E2E8F0", "#F1F5F9", "#CBD5E1", "#94A3B8", "#475569",
 *   "#64748B", "#1E293B", "#F8FAFC", "#64748B"] };
 */

export const ThemeProps = {
  fontWeights: {
    extraLight: 400, // brand: 400-700 only
    light: 400,
    regular: 400,
    medium: 500,
    bold: 600,
  },

  inputSizes: {
    regular: { width: 208, height: 34 },
    large: { width: 224, height: 34 },
    wizard: { width: 384 },
  },

  borderRadius: "6px", // --t9-radius-md (controls); cards use 8px
  contentWidth: "928px",
  boxShadow: "box-shadow: var(--t9-shadow-card);", // 0 2px 8px rgba(44, 62, 80, 0.08) in light

  animations: {
    swift: ".45s cubic-bezier(0.3, 1, 0.4, 1) 0s",
    rotation: css`
      animation: rotate 2s infinite linear;
      @keyframes rotate {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    `,
    disabledLoading: css`
      animation: opacityToggle 750ms linear infinite alternate-reverse;
      @keyframes opacityToggle {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0.3;
        }
      }
    `,
  },

  mobileMaxWidth: 1350,

  exactWidth,
  exactHeight,
  exactSize: (size: string) => css`
    ${exactWidth(size)}
    ${exactHeight(size)}
  `,
};
